import type { Timestamp } from 'firebase/firestore';

/**
 * Mirrors `users/{userId}/ideas/{ideaId}`, the same documents the iOS and Android clients
 * read and write.
 *
 * Every field beyond `id` is optional on read even where the writing clients always set
 * it. Documents predate the current shape, and a web client that throws on an older one is
 * worse than a web client that renders it with a field missing.
 */
export interface Idea {
  id: string;
  userId?: string;
  title?: string;
  description?: string;
  tags?: string[];
  links?: string[];
  images?: string[];
  transcript?: string;
  /** Extracted by `processTranscript` at capture. Opaque here; the phones read it. */
  recallHints?: Record<string, unknown>;
  createdAt?: Timestamp;
  updatedAt?: Timestamp;
}

/**
 * Mirrors the `users/{userId}` document.
 *
 * The three name fields are the only ones this client manages. The document also carries
 * settings that belong to the phone apps — `toastDuration`, and the three voice-feedback
 * toggles — which govern behaviour the web has no equivalent of: there are no toasts here
 * and nothing reads a note aloud.
 *
 * They are declared so their existence is visible, and deliberately not editable here.
 * Offering a web control for how long an iOS toast stays on screen would be setting another
 * device's preference from a screen that cannot demonstrate the effect. **Any write to this
 * document must merge**, or editing a name from the web silently resets all four.
 */
export interface UserProfile {
  firstName?: string;
  lastName?: string;
  email?: string;

  /** Set on iOS. Seconds a reminder toast stays visible; 0 means until dismissed. */
  toastDuration?: number;
  /** Set on iOS. Master switch for reading notes aloud. */
  voiceFeedbackEnabled?: boolean;
  /** Set on iOS. Read-back for notes typed rather than spoken. */
  voiceFeedbackForText?: boolean;
  /** Set on iOS. Read-back for notes captured by voice. */
  voiceFeedbackForVoice?: boolean;

  updatedAt?: Timestamp;
}

/** The fields the web client may change. */
export interface ProfileEdits {
  firstName: string;
  lastName: string;
}

/** What the avatar shows: custom profile, then auth display name, then email, then "U". */
export function initialsFor(
  profile: UserProfile | null,
  displayName: string | null | undefined,
  email: string | null | undefined
): string {
  const first = profile?.firstName?.trim();
  const last = profile?.lastName?.trim();
  if (first || last) {
    return `${first?.[0] ?? ''}${last?.[0] ?? ''}`.toUpperCase();
  }
  const fromName = displayName?.trim();
  if (fromName) {
    const parts = fromName.split(/\s+/);
    return parts.length > 1
      ? `${parts[0]?.[0] ?? ''}${parts[parts.length - 1]?.[0] ?? ''}`.toUpperCase()
      : (fromName[0] ?? 'U').toUpperCase();
  }
  return (email?.[0] ?? 'U').toUpperCase();
}
